<script setup>
import { ref, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import * as authApi from '@/api/auth'
import * as validation from '@/utils/validation'
import { clearStoredAttribution, getAttributionPayload } from '@/utils/attribution'

const router = useRouter()
const authStore = useAuthStore()

const form = ref({
  phone: '',
  code: '',
  password: '',
  confirmPassword: '',
  nickname: '',
  company: ''
})

// UI state
const loading = ref(false)
const error = ref('')
const codeButtonLoading = ref(false)
const passwordErrors = ref([])
const agree = ref(false)

// Countdown logic
const countdown = ref(0)
let timer = null

const sendCode = async () => {
  error.value = ''

  if (!validation.validatePhone(form.value.phone)) {
    error.value = validation.getPhoneErrorMessage()
    return
  }

  codeButtonLoading.value = true
  try {
    const response = await authApi.sendVerificationCode(form.value.phone, 1) // 1 = register
    if (response.code === 1) {
      countdown.value = 60
      timer = setInterval(() => {
        countdown.value--
        if (countdown.value <= 0) {
          clearInterval(timer)
        }
      }, 1000)
    } else {
      error.value = response.message || '验证码发送失败'
    }
  } catch (err) {
    error.value = '验证码发送失败，请检查网络连接'
    console.error('Send code error:', err)
  } finally {
    codeButtonLoading.value = false
  }
}

const handleRegister = async () => {
  error.value = ''
  passwordErrors.value = []

  // Validate agreement
  if (!agree.value) {
    error.value = '请阅读并同意服务条款和隐私政策'
    return
  }

  // Validate phone
  if (!validation.validatePhone(form.value.phone)) {
    error.value = validation.getPhoneErrorMessage()
    return
  }

  // Validate code
  if (!validation.validateCode(form.value.code)) {
    error.value = validation.getCodeErrorMessage()
    return
  }

  // Validate password
  const passwordValidation = validation.validatePassword(form.value.password)
  if (!passwordValidation.isValid) {
    passwordErrors.value = passwordValidation.errors
    error.value = passwordValidation.errors[0]
    return
  }

  // Validate password match
  if (!validation.validatePasswordMatch(form.value.password, form.value.confirmPassword)) {
    error.value = validation.getPasswordMatchErrorMessage()
    return
  }

  loading.value = true
  try {
    const response = await authApi.register(
      form.value.phone,
      form.value.code,
      form.value.password,
      form.value.nickname,
      form.value.company,
      getAttributionPayload()
    )

    if (response.code === 1) {
      // Register successful - auto login: save token and user info to store
      authStore.setToken(response.data.token)
      authStore.setUserInfo({
        user_id: response.data.user_id,
        phone: response.data.phone,
        nickname: response.data.nickname,
        company: response.data.company,
        user_type: response.data.user_type
      })
      clearStoredAttribution()

      // Redirect to home
      router.push('/')
    } else {
      error.value = response.message || '注册失败'
    }
  } catch (err) {
    error.value = '注册失败，请检查网络连接'
    console.error('Register error:', err)
  } finally {
    loading.value = false
  }
}

onUnmounted(() => {
  if (timer) clearInterval(timer)
})
</script>

<template>
  <div class="auth-container">
    <div class="auth-card">
      <h2 class="auth-title">创建账号</h2>

      <!-- Error Message -->
      <div v-if="error" class="error-message">{{ error }}</div>

      <form @submit.prevent="handleRegister" class="auth-form">
        
        <div class="form-group">
          <label for="phone">手机号码</label>
          <div class="input-wrapper">
             <span class="prefix">+86</span>
             <input
              type="tel"
              id="phone"
              v-model="form.phone"
              required
              placeholder="请输入手机号码"
              maxlength="11"
              :disabled="loading || codeButtonLoading"
            />
          </div>
        </div>

        <div class="form-group">
          <label for="code">验证码</label>
          <div class="code-wrapper">
            <input
              type="text"
              id="code"
              v-model="form.code"
              required
              placeholder="6位数字验证码"
              maxlength="6"
              :disabled="loading"
            />
            <button
              type="button"
              class="btn-code"
              @click="sendCode"
              :disabled="countdown > 0 || codeButtonLoading || loading"
            >
              {{ countdown > 0 ? `${countdown}s` : '获取验证码' }}
            </button>
          </div>
        </div>

        <div class="form-group">
          <label for="password">设置密码</label>
          <input
            type="password"
            id="password"
            v-model="form.password"
            required
            placeholder="至少8位，包含大小写字母和数字"
            :disabled="loading"
          />
          <div v-if="passwordErrors.length > 0" class="password-hints">
            <div v-for="(err, idx) in passwordErrors" :key="idx" class="hint-item">
              {{ err }}
            </div>
          </div>
        </div>

        <div class="form-group">
          <label for="confirmPassword">确认密码</label>
          <input
            type="password"
            id="confirmPassword"
            v-model="form.confirmPassword"
            required
            placeholder="请再次输入密码"
            :disabled="loading"
          />
        </div>

        <div class="form-group">
          <label for="nickname">昵称 <span class="optional">(可选)</span></label>
          <input
            type="text"
            id="nickname"
            v-model="form.nickname"
            placeholder="输入您的昵称"
            :disabled="loading"
          />
        </div>

        <div class="form-group">
          <label for="company">公司 <span class="optional">(可选)</span></label>
          <input
            type="text"
            id="company"
            v-model="form.company"
            placeholder="输入您的公司名称"
            :disabled="loading"
          />
        </div>

        <!-- 服务条款和隐私政策勾选 -->
        <div class="form-group agreement">
          <label class="checkbox-label">
            <input 
              type="checkbox" 
              v-model="agree" 
              :disabled="loading"
            />
            <span>
              我已阅读并同意 
              <router-link to="/terms">服务条款</router-link> 
              与 
              <router-link to="/privacy">隐私政策</router-link>
            </span>
          </label>
        </div>

        <button type="submit" class="btn-submit" :disabled="loading">
          {{ loading ? '正在注册...' : '立即注册' }}
        </button>
      </form>
      <div class="auth-footer">
        <p>已有账号？ <router-link to="/login">立即登录</router-link></p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.auth-container {
  min-height: calc(100vh - 200px);
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f4f6f9;
  padding: 40px 20px;
}

.auth-card {
  background: white;
  padding: 40px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  width: 100%;
  max-width: 460px;
  border-top: 4px solid #005eb8;
}

.auth-title {
  font-size: 24px;
  margin-bottom: 24px;
  color: #1a1a1a;
  text-align: center;
}

.error-message {
  background-color: #fff2f0;
  border: 1px solid #ffccc7;
  color: #d84315;
  padding: 12px 16px;
  border-radius: 4px;
  margin-bottom: 20px;
  font-size: 14px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group.agreement {
  margin-bottom: 24px;
  padding: 12px;
  background: #f9f9f9;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.form-group label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #333;
}

.checkbox-label {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  color: #555;
  cursor: pointer;
  font-weight: normal;
  margin: 0;
}

.checkbox-label input[type="checkbox"] {
  margin-top: 3px;
  cursor: pointer;
  flex-shrink: 0;
}

.checkbox-label a {
  color: #005eb8;
  text-decoration: none;
  transition: color 0.2s;
}

.checkbox-label a:hover {
  color: #004a94;
  text-decoration: underline;
}

.optional {
  font-weight: normal;
  color: #999;
  font-size: 12px;
}

.password-hints {
  background-color: #fef5f0;
  border: 1px solid #ffe7da;
  border-radius: 4px;
  margin-top: 6px;
  padding: 8px 12px;
}

.hint-item {
  font-size: 12px;
  color: #d84315;
  margin: 4px 0;
}

.hint-item:first-child {
  margin-top: 0;
}

.hint-item:last-child {
  margin-bottom: 0;
}

.input-wrapper {
  display: flex;
  align-items: center;
  border: 1px solid #ccc;
  border-radius: 4px;
  overflow: hidden;
}

.prefix {
  background: #f5f5f5;
  padding: 10px 12px;
  font-size: 14px;
  color: #666;
  border-right: 1px solid #ccc;
}

.input-wrapper input {
  border: none;
  flex: 1;
  padding: 10px 12px;
  font-size: 14px;
  outline: none;
}

.input-wrapper:focus-within {
  border-color: #005eb8;
}

/* Standard input (password) */
.form-group > input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  outline: none;
}
.form-group > input:focus {
  border-color: #005eb8;
}

.code-wrapper {
  display: flex;
  gap: 10px;
}

.code-wrapper input {
  flex: 1;
  padding: 10px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  outline: none;
}

.code-wrapper input:focus {
  border-color: #005eb8;
}

.btn-code {
  padding: 0 16px;
  background: #eef4fb;
  color: #005eb8;
  border: 1px solid #cce0f5;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  min-width: 100px;
  transition: all 0.2s;
}

.btn-code:hover:not(:disabled) {
  background: #dbe9f6;
}

.btn-code:disabled {
  background: #f5f5f5;
  color: #999;
  border-color: #ddd;
  cursor: not-allowed;
}

.btn-submit {
  width: 100%;
  padding: 12px;
  background-color: #005eb8;
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
  font-size: 16px;
  transition: background-color 0.2s;
  margin-top: 10px;
}

.btn-submit:hover {
  background-color: #004a94;
}

.auth-footer {
  margin-top: 24px;
  text-align: center;
  font-size: 14px;
  color: #666;
}

.auth-footer a {
  color: #005eb8;
  text-decoration: none;
  font-weight: 600;
}

.auth-footer a:hover {
  text-decoration: underline;
}
</style>
